// src/hooks/useScreenerData.js
// SCREENER-FRAG — new file. Logic extracted from ScreenerPage.jsx (previously
// inline state + handlers: fetchResults/handleScan/handleSignalFilter/
// handleSectorFilter/handleSort). Content of each handler is unchanged in
// behavior; the boundary changed — this hook knows nothing about D3 signal
// logging or saved scans. It reports what happened via callbacks
// (onFetchComplete, onScanUsed) so useScreenerSignal and useSavedScans can
// react without this hook needing to import or know about either.
//
// PIECE 2 addition: registryFilters — values for the new field-registry
// "ready" fields (industry, country, edgarFilingTier). Sector stays on its
// own dedicated sectorFilter state (unchanged) since it already has a
// working dropdown — see FilterPanel.jsx's excludeKeys for why it isn't
// duplicated there.
//
// applyLoadedScan(filters) — used by the shell's onLoadScan wrapper (from
// SavedScansPanel) to apply a saved scan's filters and re-fetch, without
// SavedScansPanel needing to know these setters exist.

import { useState, useCallback, useEffect } from 'react';
import { API_URL, SCAN_D3_MAP } from '../constants/screenerConfig';

export function useScreenerData({ onFetchComplete, onScanUsed }) {
  const [results,      setResults]      = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [dataAsOf,     setDataAsOf]     = useState(null);
  const [marketStatus, setMarketStatus] = useState(null);
  const [isStale,      setIsStale]      = useState(false);

  const [activeScan,   setActiveScan]   = useState('');
  const [signalFilter, setSignalFilter] = useState('ALL');
  const [sectorFilter, setSectorFilter] = useState('ALL');
  const [sortBy,       setSortBy]       = useState('score');

  // PIECE 2 — ready registry fields beyond sector (industry, country,
  // edgarFilingTier). Stub fields never reach here — FilterPanel disables
  // their controls, so onChange never fires for them.
  const [registryFilters, setRegistryFiltersState] = useState({});

  function setRegistryFilter(key, value) {
    setRegistryFiltersState(prev => ({ ...prev, [key]: value }));
  }

  const fetchResults = useCallback(async (opts = {}) => {
    const signal = opts.signal  ?? signalFilter;
    const sector = opts.sector  ?? sectorFilter;
    const sort   = opts.sort    ?? sortBy;
    const scan   = opts.scan    ?? activeScan;
    const registry = opts.registryFilters ?? registryFilters;

    const params = new URLSearchParams({ signal, sector, sort });
    if (scan) params.set('scan', scan);
    // PIECE 2 — only send ready registry fields that have a real value set
    Object.entries(registry).forEach(([key, value]) => {
      if (value != null && value !== 'Any' && value !== '') {
        params.set(key, typeof value === 'object' ? JSON.stringify(value) : value);
      }
    });

    setLoading(true);
    setError('');
    try {
      const res  = await fetch(`${API_URL}/api/screener/results?${params}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Screener unavailable');

      setResults(data.results || []);
      setDataAsOf(data.dataAsOf);
      setMarketStatus(data.marketStatus);
      setIsStale(!!data.isStale);

      const filters = {
        signal, sector, sort,
        scanUsed:   scan || null,
        d3Strategy: scan ? (SCAN_D3_MAP[scan] || null) : null,
        ...registry,
      };
      onFetchComplete?.(filters, data.total || 0, data.results || []);
    } catch (e) {
      setError(e.message || 'Screener temporarily unavailable.');
    } finally {
      setLoading(false);
    }
  }, [signalFilter, sectorFilter, sortBy, activeScan, registryFilters, onFetchComplete]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  function handleScan(key) {
    const next = activeScan === key ? '' : key;
    onScanUsed?.(next);
    setActiveScan(next);
    setSignalFilter('ALL');
    setSectorFilter('ALL');
    fetchResults({ scan: next, signal: 'ALL', sector: 'ALL', sort: sortBy });
  }

  function handleSignalFilter(v) {
    setSignalFilter(v);
    setActiveScan('');
    fetchResults({ signal: v, sector: sectorFilter, sort: sortBy, scan: '' });
  }

  function handleSectorFilter(v) {
    setSectorFilter(v);
    setActiveScan('');
    fetchResults({ signal: signalFilter, sector: v, sort: sortBy, scan: '' });
  }

  function handleSort(v) {
    setSortBy(v);
    fetchResults({ signal: signalFilter, sector: sectorFilter, sort: v, scan: activeScan });
  }

  // PIECE 2 — registry field change (FilterPanel onChange)
  function handleRegistryFilterChange(key, value) {
    setRegistryFilter(key, value);
    fetchResults({ registryFilters: { ...registryFilters, [key]: value } });
  }

  // Used by the shell's onLoadScan wrapper — applies a saved scan's filters
  function applyLoadedScan(filters) {
    setSignalFilter(filters.signal || 'ALL');
    setSectorFilter(filters.sector || 'ALL');
    setSortBy(filters.sort || 'score');
    setActiveScan(filters.scanUsed || '');
    fetchResults({
      signal: filters.signal || 'ALL',
      sector: filters.sector || 'ALL',
      sort:   filters.sort || 'score',
      scan:   filters.scanUsed || '',
    });
  }

  return {
    results, loading, error, dataAsOf, marketStatus, isStale,
    activeScan, signalFilter, sectorFilter, sortBy, registryFilters,
    fetchResults, handleScan, handleSignalFilter, handleSectorFilter, handleSort,
    handleRegistryFilterChange, applyLoadedScan,
  };
}
